import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { BackofficeTournamentsApiService } from '../core/backoffice-tournaments-api.service';
import { I18nService } from '../core/i18n.service';
import { MatchScoreReportsApiService } from '../core/match-score-reports-api.service';
import { MatchScoreReportPayload } from '../core/match-score-reports-api.types';
import { SessionService } from '../core/session.service';
import { TeamsApiService } from '../core/teams-api.service';
import { TournamentsApiService } from '../core/tournaments-api.service';
import { BracketMatch, BracketReadResponse } from '../core/tournaments-api.types';
import {
  BracketBoardView,
  BracketStageView,
  BracketStageKey,
  BracketRoundView,
  stagePriority,
  mapBracketReadResponseToBoardView
} from './tournament-bracket-page.models';
import { TournamentBracketRoundColumnComponent } from './tournament-bracket-round-column.component';

@Component({
  selector: 'app-tournament-bracket-page',
  standalone: true,
  imports: [CommonModule, RouterLink, TournamentBracketRoundColumnComponent],
  templateUrl: './tournament-bracket-page.component.html',
  styleUrl: './tournament-bracket-page.component.scss'
})
export class TournamentBracketPageComponent {
  private static readonly DEFAULT_CARD_HEIGHT_REM = 6.75;
  private static readonly DEFAULT_BASE_GAP_REM = 0.75;
  private static readonly DE_COMPACT_CARD_HEIGHT_REM = 4.25;
  private static readonly DE_COMPACT_BASE_GAP_REM = 0.32;
  private static readonly DE_COMPACT_CONNECTOR_SPAN_REM = 0.86;
  private static readonly EDITABLE_SCORE_REPORT_STATUSES = new Set(['reported', 'in_review', 'rejected']);
  private static readonly CONTESTABLE_SCORE_REPORT_STATUSES = new Set(['reported', 'in_review']);

  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(TournamentsApiService);
  private readonly backofficeApi = inject(BackofficeTournamentsApiService);
  private readonly teamsApi = inject(TeamsApiService);
  private readonly scoreReportsApi = inject(MatchScoreReportsApiService);
  private readonly i18n = inject(I18nService);
  protected readonly session = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly slug = signal('');
  protected readonly backofficePreview = signal(false);
  protected readonly fromMyMatches = signal(false);
  protected readonly bracketData = signal<BracketReadResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly reportScoreHrefByMatchId = signal<ReadonlyMap<number, string>>(new Map());
  protected readonly reportScoreLabelByMatchId = signal<ReadonlyMap<number, string>>(new Map());

  protected readonly board = computed<BracketBoardView | null>(() => {
    const data = this.bracketData();
    return data ? mapBracketReadResponseToBoardView(data) : null;
  });

  protected readonly rounds = computed(() => this.board()?.rounds ?? []);
  protected readonly stages = computed(() => this.board()?.stages ?? []);
  protected readonly isDoubleEliminationBoard = computed(() => {
    const data = this.board();
    if (!data) return false;
    if (data.bracket.bracket_kind === 'double_elimination') return true;
    const keys = new Set(this.stageSections().map((section) => section.key));
    return keys.has('winner') && keys.has('loser');
  });
  protected readonly isSingleEliminationBoard = computed(() => {
    const data = this.board();
    if (!data) return false;
    return data.bracket.bracket_kind === 'simple_single';
  });
  protected readonly stageSections = computed(() => {
    const rounds = this.rounds();
    if (rounds.length === 0) return [];

    const grouped = new Map<BracketStageKey, BracketRoundView[]>();
    for (const round of rounds) {
      const list = grouped.get(round.stage) ?? [];
      list.push(round);
      grouped.set(round.stage, list);
    }

    return Array.from(grouped.entries())
      .sort((a, b) => stagePriority(a[0]) - stagePriority(b[0]))
      .map(([key, stageRounds]) => ({
        key,
        rounds: [...stageRounds].sort((a, b) => {
          if (a.position !== b.position) return a.position - b.position;
          return a.round_number - b.round_number;
        })
      }));
  });
  protected readonly singleRoundMetrics = computed(() => {
    if (!this.isSingleEliminationBoard()) return new Map<number, { shellOffset: number; matchOffset: number; matchGap: number }>();
    return this.buildRoundMetrics(
      this.rounds(),
      TournamentBracketPageComponent.DEFAULT_CARD_HEIGHT_REM,
      TournamentBracketPageComponent.DEFAULT_BASE_GAP_REM
    );
  });
  protected readonly deLayout = computed(() => {
    const sectionWinner = this.stageSections().find((section) => section.key === 'winner')?.rounds ?? [];
    const sectionLoser = this.stageSections().find((section) => section.key === 'loser')?.rounds ?? [];
    const sectionFinal = this.stageSections().find((section) => section.key === 'final')?.rounds ?? [];

    const winner = [...sectionWinner];
    const loser = [...sectionLoser];
    const final = [...sectionFinal];

    if (winner.length === 0 || loser.length === 0 || final.length === 0) {
      for (const round of this.rounds()) {
        const resolvedStage = this.resolveDesktopStage(round);
        if (resolvedStage === 'loser' && loser.every((existing) => existing.id !== round.id)) {
          loser.push(round);
        } else if (resolvedStage === 'final' && final.every((existing) => existing.id !== round.id)) {
          final.push(round);
        } else if (resolvedStage === 'winner' && winner.every((existing) => existing.id !== round.id)) {
          winner.push(round);
        }
      }
    }

    winner.sort((a, b) => (a.position !== b.position ? a.position - b.position : a.round_number - b.round_number));
    loser.sort((a, b) => (a.position !== b.position ? a.position - b.position : a.round_number - b.round_number));
    final.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position;
      return a.round_number - b.round_number;
    });

    const grandFinal = final.find((round) => round.matches.some((match) => match.reset_match_id));
    if (grandFinal) {
      const resetMatchId = grandFinal.matches.find((match) => match.reset_match_id)?.reset_match_id ?? null;
      const resetRound = final.find((round) => round.matches.some((match) => match.id === resetMatchId));
      if (resetRound) {
        const withoutPair = final.filter((round) => round.id !== grandFinal.id && round.id !== resetRound.id);
        withoutPair.push(grandFinal, resetRound);
        return { winner, loser, final: withoutPair };
      }
    }

    return { winner, loser, final };
  });
  protected readonly deWinnerLocalColumnMap = computed(() => {
    const map = new Map<number, number>();
    this.deLayout().winner.forEach((round, index) => map.set(round.id, index + 1));
    return map;
  });
  protected readonly deLoserLocalColumnMap = computed(() => {
    const map = new Map<number, number>();
    this.deLayout().loser.forEach((round, index) => map.set(round.id, index + 1));
    return map;
  });
  protected readonly deWinnerLocalMax = computed(() => this.deLayout().winner.length || 1);
  protected readonly deLoserLocalMax = computed(() => this.deLayout().loser.length || 1);
  protected readonly deWinnerRoundMetrics = computed(() => this.buildDeRoundMetrics(this.deLayout().winner));
  protected readonly deLoserRoundMetrics = computed(() => this.buildDeRoundMetrics(this.deLayout().loser));
  protected readonly deFinalRoundColumnMap = computed(() => {
    const roundToColumn = new Map<number, number>();
    const winnerLocalMap = this.deWinnerLocalColumnMap();
    const loserLocalMap = this.deLoserLocalColumnMap();

    for (const round of [...this.deLayout().winner, ...this.deLayout().loser]) {
      const roundCol =
        winnerLocalMap.get(round.id) ??
        loserLocalMap.get(round.id) ??
        Math.max(1, round.position);
      roundToColumn.set(round.id, roundCol);
    }

    const finalAnchorCol = this.isDe32WithReset()
      ? Math.max(this.deWinnerLocalMax() + 1, this.deLoserLocalMax() - 1)
      : this.deWinnerLocalMax() + 1;

    let nextCol = finalAnchorCol;
    for (const round of this.deLayout().final) {
      const col = nextCol;
      roundToColumn.set(round.id, col);
      nextCol = col + 1;
    }

    return roundToColumn;
  });
  protected readonly deVisualColumnCount = computed(() => {
    const finalCols = Array.from(this.deFinalRoundColumnMap().values());
    const maxFinal = finalCols.length > 0 ? Math.max(...finalCols) : 0;
    return Math.max(this.deWinnerLocalMax(), this.deLoserLocalMax(), maxFinal);
  });
  protected readonly isDe16WithReset = computed(() => {
    if (!this.isDoubleEliminationBoard()) return false;
    const firstWinnerRound = this.deLayout().winner[0];
    const is16 = Boolean(firstWinnerRound && firstWinnerRound.matches.length === 8);
    const hasReset = this.deLayout().final.some((round) => round.matches.some((match) => match.reset_match_id));
    return is16 && hasReset;
  });
  protected readonly isDe32WithReset = computed(() => {
    if (!this.isDoubleEliminationBoard()) return false;
    const firstWinnerRound = this.deLayout().winner[0];
    const is32 = Boolean(firstWinnerRound && firstWinnerRound.matches.length === 16);
    const hasReset = this.deLayout().final.some((round) => round.matches.some((match) => match.reset_match_id));
    return is32 && hasReset;
  });

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  constructor() {
    effect(() => {
      const response = this.bracketData();
      const isAuthenticated = this.session.isAuthenticated();
      const fromMyMatches = this.fromMyMatches();
      const backofficePreview = this.backofficePreview();

      if (!response || !isAuthenticated || backofficePreview) {
        this.reportScoreHrefByMatchId.set(new Map());
        this.reportScoreLabelByMatchId.set(new Map());
        return;
      }

      void fromMyMatches;
      this.loadReportScoreEntryPoints(response);
    });

    this.backofficePreview.set(this.route.snapshot.data['backofficePreview'] === true);

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          this.fromMyMatches.set(params.get('from') === 'my-matches');
        }
      });

    this.route.paramMap
      .pipe(
        switchMap((params) => {
          if (this.backofficePreview()) {
            const tournamentId = Number(params.get('tournamentId') ?? '0');
            this.slug.set('');
            return this.backofficeApi.getBracket(tournamentId);
          }

          const slug = params.get('slug') ?? '';
          this.slug.set(slug);
          return this.api.getTournamentBracket(slug);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.bracketData.set(response);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(this.i18n.t(this.backofficePreview() ? 'bo.bracketPreview.error' : 'public.bracket.error'));
          this.reportScoreHrefByMatchId.set(new Map());
          this.reportScoreLabelByMatchId.set(new Map());
          this.loading.set(false);
        }
      });
  }

  protected bracketKindLabel(kind: string): string {
    if (kind === 'simple_single') {
      return this.i18n.t('public.bracket.kind.simple');
    }

    return kind;
  }

  protected stageLabel(stage: BracketStageView['key']): string {
    if (stage === 'winner') return 'Winner bracket';
    if (stage === 'loser') return 'Loser bracket';
    if (stage === 'final') return 'Final stage';
    return 'Other stage';
  }

  protected backRoute(): string[] {
    if (this.backofficePreview()) {
      return ['/backoffice/tournaments'];
    }
    return this.fromMyMatches() ? ['/player/matches'] : ['/tournaments'];
  }

  protected backLabel(): string {
    if (this.backofficePreview()) {
      return this.t('bo.bracketPreview.back');
    }
    return this.fromMyMatches() ? this.t('public.bracket.backToMatches') : this.t('public.bracket.back');
  }

  protected pageEyebrow(): string {
    return this.backofficePreview() ? this.t('bo.bracketPreview.eyebrow') : this.t('public.bracket.titleSuffix');
  }

  protected pageLegend(bracketStatus: string | null | undefined): string {
    if (!this.backofficePreview()) {
      return this.t('public.bracket.readOnly');
    }

    if (bracketStatus === 'draft') {
      return this.t('bo.bracketPreview.draftLegend');
    }

    return this.t('bo.bracketPreview.publishedLegend');
  }

  protected reportScoreQueryParams(): Record<string, string> | null {
    if (this.backofficePreview()) return null;
    return this.fromMyMatches() ? { from: 'my-matches' } : null;
  }

  private loadReportScoreEntryPoints(response: BracketReadResponse): void {
    this.reportScoreHrefByMatchId.set(new Map());
    this.reportScoreLabelByMatchId.set(new Map());

    if (!this.session.isAuthenticated()) {
      return;
    }

    const tournamentSlug = response.tournament.slug;

    this.teamsApi.getMyTeams()
      .pipe(
        switchMap(({ teams }) => {
          const myTeamIds = new Set(teams.map((team) => team.id));
          const candidateMatches = response.bracket.rounds
            .flatMap((round) => round.matches)
            .filter((match) => this.isCandidateReportableMatch(match, myTeamIds));

          if (candidateMatches.length === 0) {
            return of({ hrefMap: new Map<number, string>(), labelMap: new Map<number, string>() });
          }

          return forkJoin(
            candidateMatches.map((match) =>
              this.scoreReportsApi.getReport(tournamentSlug, match.id).pipe(
                map(({ score_report }) => ({
                  matchId: match.id,
                  entry: this.resolvePlayerScoreReportEntry(score_report, myTeamIds, tournamentSlug, match.id)
                })),
                catchError(() => of({ matchId: match.id, entry: null }))
              )
            )
          ).pipe(
            map((entries) => {
              const hrefMap = new Map<number, string>();
              const labelMap = new Map<number, string>();
              for (const entry of entries) {
                if (entry.entry?.href) {
                  hrefMap.set(entry.matchId, entry.entry.href);
                  labelMap.set(entry.matchId, entry.entry.label);
                }
              }
              return { hrefMap, labelMap };
            })
          );
        }),
        catchError(() => of({ hrefMap: new Map<number, string>(), labelMap: new Map<number, string>() })),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ hrefMap, labelMap }) => {
        if (this.slug() !== tournamentSlug || !this.session.isAuthenticated()) {
          return;
        }

        this.reportScoreHrefByMatchId.set(hrefMap);
        this.reportScoreLabelByMatchId.set(labelMap);
      });
  }

  protected deGridColumn(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): number {
    if (lane === 'winner') {
      return this.deWinnerLocalColumnMap().get(round.id) ?? Math.max(1, round.position);
    }

    if (lane === 'loser') {
      return this.deLoserLocalColumnMap().get(round.id) ?? Math.max(1, round.position);
    }

    return this.deFinalRoundColumnMap().get(round.id) ?? Math.max(1, round.position);
  }

  protected deShellOffsetRem(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): number {
    if (lane === 'winner') {
      return this.deWinnerRoundMetrics().get(round.id)?.shellOffset ?? 0;
    }

    if (lane === 'loser') {
      return this.deLoserRoundMetrics().get(round.id)?.shellOffset ?? 0;
    }

    return 0;
  }

  protected deRoundMatchOffsetRem(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): number | null {
    if (lane === 'winner') {
      return this.deWinnerRoundMetrics().get(round.id)?.matchOffset ?? null;
    }

    if (lane === 'loser') {
      return this.deLoserRoundMetrics().get(round.id)?.matchOffset ?? null;
    }

    return null;
  }

  protected deRoundMatchGapRem(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): number | null {
    if (lane === 'winner') {
      return this.deWinnerRoundMetrics().get(round.id)?.matchGap ?? null;
    }

    if (lane === 'loser') {
      return this.deLoserRoundMetrics().get(round.id)?.matchGap ?? null;
    }

    return null;
  }

  protected singleRoundMatchOffsetRem(round: BracketRoundView): number | null {
    return this.singleRoundMetrics().get(round.id)?.matchOffset ?? null;
  }

  protected singleRoundMatchGapRem(round: BracketRoundView): number | null {
    return this.singleRoundMetrics().get(round.id)?.matchGap ?? null;
  }

  protected singleRoundShellOffsetRem(round: BracketRoundView): number {
    return this.singleRoundMetrics().get(round.id)?.shellOffset ?? 0;
  }

  protected isDeGrandFinalRound(round: BracketRoundView): boolean {
    return round.matches.some((match) => match.reset_match_id);
  }

  protected isDeResetFinalRound(round: BracketRoundView): boolean {
    if (this.isDeGrandFinalRound(round)) return false;

    const resetTargetIds = new Set(
      this.deLayout()
        .final.flatMap((finalRound) => finalRound.matches.map((match) => match.reset_match_id))
        .filter((id): id is number => typeof id === 'number')
    );
    return round.matches.some((match) => resetTargetIds.has(match.id));
  }

  protected isDeResetRoundNotNeeded(round: BracketRoundView): boolean {
    if (!this.isDeResetFinalRound(round)) return false;

    const resetMatch = round.matches[0];
    if (!resetMatch || resetMatch.winner_team) return false;
    if (resetMatch.slots.some((slot) => slot.team != null)) return false;

    const grandFinalRound = this.deLayout().final.find((candidate) => this.isDeGrandFinalRound(candidate));
    const grandFinalMatch = grandFinalRound?.matches[0];
    if (!grandFinalMatch?.winner_team) return false;

    const winnerBracketChampionId = grandFinalMatch.slots.find((slot) => slot.slot_number === 1)?.team?.id ?? null;
    return winnerBracketChampionId != null && grandFinalMatch.winner_team.id === winnerBracketChampionId;
  }

  protected deFinalRoundStatusLabel(round: BracketRoundView): string | null {
    if (this.isDeResetRoundNotNeeded(round)) {
      return 'Not needed';
    }

    return null;
  }

  protected feedsDeGrandFinal(round: BracketRoundView): boolean {
    const grandFinalMatchId = this.deGrandFinalMatchId();
    if (!grandFinalMatchId) return false;

    const feedsByArrow = round.matches.some(
      (match) => match.winner_to_match_id === grandFinalMatchId || match.loser_to_match_id === grandFinalMatchId
    );
    const feedsBySlotSource = this.deGrandFinalSourceMatchIds().some((sourceId) =>
      round.matches.some((match) => match.id === sourceId)
    );

    return feedsByArrow || feedsBySlotSource;
  }

  protected showDeForwardFeed(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): boolean {
    return this.deForwardFeedSpanRem(round, lane) > 0;
  }

  protected useDeOutgoingFeed(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): boolean {
    if (!this.showDeForwardFeed(round, lane)) return false;
    return true;
  }

  protected hideDeIncomingConnector(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): boolean {
    if (lane !== 'final') return false;

    // On DE16 we want Winner Final -> Grand Final to read like a standard
    // outgoing lane from match 27, so we hide the extra incoming stroke on
    // the grand final card itself.
    return this.isDe16WithReset() && this.isDeGrandFinalRound(round);
  }

  protected deForwardFeedSpanRem(round: BracketRoundView, lane: 'winner' | 'loser' | 'final'): number {
    if (!this.feedsDeGrandFinal(round)) return 0;
    if (lane !== 'winner') return 0;

    const currentCol = this.deGridColumn(round, lane);
    const targetCol = this.deGrandFinalColumn();
    if (targetCol == null || targetCol <= currentCol) return 0;

    if (targetCol === currentCol + 1) {
      return TournamentBracketPageComponent.DE_COMPACT_CONNECTOR_SPAN_REM;
    }

    const skippedColumns = targetCol - currentCol - 1;
    const columnWidth = this.isDe16WithReset() ? 11.75 : 12.875;
    const columnGap = this.isDe16WithReset() ? 0.34 : 0.48;
    const feedTail = 1.2;
    return Number((skippedColumns * (columnWidth + columnGap) + columnGap + feedTail).toFixed(3));
  }

  private deGrandFinalMatchId(): number | null {
    const grandRound = this.deLayout().final.find((round) => round.matches.some((match) => match.reset_match_id));
    return grandRound?.matches[0]?.id ?? null;
  }

  private deGrandFinalColumn(): number | null {
    const grandRound = this.deLayout().final.find((round) => round.matches.some((match) => match.reset_match_id));
    if (!grandRound) return null;
    return this.deFinalRoundColumnMap().get(grandRound.id) ?? null;
  }

  private deGrandFinalSourceMatchIds(): number[] {
    const grandRound = this.deLayout().final.find((round) => round.matches.some((match) => match.reset_match_id));
    const grandMatch = grandRound?.matches[0];
    if (!grandMatch) return [];
    return grandMatch.slots
      .map((slot) => slot.source_match_id)
      .filter((id): id is number => typeof id === 'number');
  }

  private buildDeRoundMetrics(rounds: BracketRoundView[]): Map<number, { shellOffset: number; matchOffset: number; matchGap: number }> {
    return this.buildRoundMetrics(
      rounds,
      TournamentBracketPageComponent.DE_COMPACT_CARD_HEIGHT_REM,
      TournamentBracketPageComponent.DE_COMPACT_BASE_GAP_REM
    );
  }

  private buildRoundMetrics(
    rounds: BracketRoundView[],
    cardHeight: number,
    baseGap: number
  ): Map<number, { shellOffset: number; matchOffset: number; matchGap: number }> {
    const metrics = new Map<number, { shellOffset: number; matchOffset: number; matchGap: number }>();
    const laneMatchCenters = new Map<number, number>();
    const basePitch = cardHeight + baseGap;

    for (const round of rounds) {
      const centers = round.matches.map((match, index) => {
        const sourceCenters = match.slots
          .map((slot) => slot.source_match_id)
          .filter((id): id is number => typeof id === 'number')
          .map((id) => laneMatchCenters.get(id))
          .filter((value): value is number => typeof value === 'number');

        if (sourceCenters.length > 0) {
          return (Math.min(...sourceCenters) + Math.max(...sourceCenters)) / 2;
        }

        return cardHeight / 2 + index * basePitch;
      });

      const starts = centers.map((center) => center - cardHeight / 2);
      const shellOffset = Math.max(0, Number(Math.min(...starts).toFixed(3)));
      const relativeStarts = starts.map((start) => Number((start - shellOffset).toFixed(3)));
      const matchOffset = relativeStarts[0] ?? 0;
      const matchGap =
        relativeStarts.length > 1
          ? Number((relativeStarts[1] - relativeStarts[0] - cardHeight).toFixed(3))
          : baseGap;

      metrics.set(round.id, {
        shellOffset,
        matchOffset,
        matchGap
      });

      round.matches.forEach((match, index) => {
        laneMatchCenters.set(match.id, centers[index] ?? cardHeight / 2);
      });
    }

    return metrics;
  }

  private resolveDesktopStage(round: BracketRoundView): BracketStageKey {
    const title = round.title.toLowerCase();
    if (title.includes('loser')) return 'loser';
    if (title.includes('grand final') || title.includes('reset') || title.includes('final stage')) return 'final';
    if (round.stage === 'loser' || round.stage === 'final' || round.stage === 'winner') return round.stage;
    return 'winner';
  }

  private isCandidateReportableMatch(match: BracketMatch, myTeamIds: ReadonlySet<number>): boolean {
    if (match.winner_team) return false;

    const matchTeamIds = this.matchTeamIds(match);
    if (matchTeamIds.length !== 2) return false;

    return matchTeamIds.some((teamId) => myTeamIds.has(teamId));
  }

  private matchTeamIds(match: BracketMatch): number[] {
    return (match.slots ?? [])
      .map((slot) => slot.team?.id ?? null)
      .filter((teamId): teamId is number => typeof teamId === 'number');
  }

  private canCurrentPlayerEditReport(
    status: string | null,
    reportingTeamId: number | null,
    myTeamIds: ReadonlySet<number>
  ): boolean {
    if (!status) {
      return true;
    }

    if (!reportingTeamId || !myTeamIds.has(reportingTeamId)) {
      return false;
    }

    return TournamentBracketPageComponent.EDITABLE_SCORE_REPORT_STATUSES.has(status);
  }

  private canCurrentPlayerContestReport(
    report: MatchScoreReportPayload | null,
    myTeamIds: ReadonlySet<number>
  ): boolean {
    if (!report?.status || !report.reporting_team_id) {
      return false;
    }

    if (!TournamentBracketPageComponent.CONTESTABLE_SCORE_REPORT_STATUSES.has(report.status)) {
      return false;
    }

    return !myTeamIds.has(report.reporting_team_id);
  }

  private resolvePlayerScoreReportEntry(
    report: MatchScoreReportPayload | null,
    myTeamIds: ReadonlySet<number>,
    tournamentSlug: string,
    matchId: number
  ): { href: string; label: string } | null {
    const href = `/tournaments/${tournamentSlug}/matches/${matchId}/score-report`;

    if (this.canCurrentPlayerEditReport(report?.status ?? null, report?.reporting_team_id ?? null, myTeamIds)) {
      return { href, label: this.t('public.bracket.reportScore') };
    }

    if (this.canCurrentPlayerContestReport(report, myTeamIds)) {
      return { href, label: this.t('public.bracket.contestReport') };
    }

    return null;
  }
}
